import React, { createContext, useContext, useEffect, useState } from "react";
import { WhiteLabelConfigurations } from "./core/models/entities/Profile";
import { useProfileContext } from "./contexts/ProfileContext";

interface WhiteLabelProviderContext {
  whiteLabel: WhiteLabelConfigurations;
  updateWhiteLabelContext: (value: WhiteLabelConfigurations) => void;
  reset: () => void;
}

const WhiteLabelContext = createContext<WhiteLabelProviderContext | undefined>(
  undefined
);

interface RepositoriesProviderProps {
  children: React.ReactNode;
}

function WhiteLabelProvider({
  children,
}: RepositoriesProviderProps): React.ReactElement {
  const { profile } = useProfileContext();

  function defaultConfigurations(): WhiteLabelConfigurations {
    return {
      colorHex: "#7BBB58",
      logoUrl: "",
      whiteLabelContactEmail: "post@jetsport.dk",
      whiteLabelContactPhone: "+45 86 61 12 55",
      whiteLabelCopyrightName: "Jet Sport",
      whiteLabelVendorName: "Jet Sport",
      whiteLabelWebsiteLink: "jetsport.dk",
    };
  }

  function getWhiteLabelWithDefaults(
    config: WhiteLabelConfigurations | undefined
  ): WhiteLabelConfigurations {
    const defaults = defaultConfigurations();
    if (!config) return defaults;
    return {
      colorHex: config.colorHex || defaults.colorHex,
      logoUrl: config.logoUrl || defaults.logoUrl,
      whiteLabelContactEmail:
        config.whiteLabelContactEmail || defaults.whiteLabelContactEmail,
      whiteLabelContactPhone:
        config.whiteLabelContactPhone || defaults.whiteLabelContactPhone,
      whiteLabelCopyrightName:
        config.whiteLabelCopyrightName || defaults.whiteLabelCopyrightName,
      whiteLabelVendorName:
        config.whiteLabelVendorName || defaults.whiteLabelVendorName,
      whiteLabelWebsiteLink:
        config.whiteLabelWebsiteLink || defaults.whiteLabelWebsiteLink,
    };
  }

  const whiteLabel = getWhiteLabelWithDefaults(
    profile?.whiteLabelConfigurations
  );

  useEffect(() => {
    setCurrentWhiteLabel(whiteLabel);
  }, [profile]);

  const [currentWhiteLabel, setCurrentWhiteLabel] =
    useState<WhiteLabelConfigurations>(whiteLabel);

  function reset() {
    setCurrentWhiteLabel({ ...profile!.whiteLabelConfigurations });
  }

  function updateWhiteLabel(value: WhiteLabelConfigurations) {
    const test = { ...value };
    setCurrentWhiteLabel(test);
  }

  return (
    <WhiteLabelContext.Provider
      value={{
        whiteLabel: currentWhiteLabel,
        updateWhiteLabelContext: updateWhiteLabel,
        reset: reset,
      }}
    >
      {children}
    </WhiteLabelContext.Provider>
  );
}

function useWhiteLabelContext(): WhiteLabelProviderContext {
  const context = useContext(WhiteLabelContext);
  if (context === undefined) {
    throw new Error(
      "useRepositoriesContext must be used within a RepositoriesProvider"
    );
  }
  return context;
}

export { WhiteLabelProvider, useWhiteLabelContext };
